import { create } from 'zustand';
import {
  fetchMe,
  saveUiPreferences,
  setActiveOrganization,
  type TrialStatus,
  type UiPreferences,
} from '../lib/api.js';
import { retryOnRateLimit } from '../lib/rate-limit-retry.js';
import { startClientErrorReporting } from '../lib/report-collector.js';

/**
 * Session web-adminu. Krok S3: JEDINE misto v `apps/web-admin/src`, kde smi
 * byt `localStorage`/`sessionStorage` — vynucuje to eslint
 * (`no-restricted-globals` v `eslint.config.js`). Vsechno ostatni (koncepty
 * zakazek, odpovedi na otazky, mapovani behu auditu) uz zije v DB za API.
 */

export interface SessionUser {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly roles: readonly string[];
  readonly tenantId: string;
  /** Krok NAP3 bod 2 — portret maklere (`data:` URI z `user.image`); null = zadny. */
  readonly portrait?: string | null;
  readonly officeId?: string;
  readonly teamIds?: readonly string[];
}

export interface TenantInfo {
  readonly id: string;
  readonly name: string;
  readonly offices: readonly { id: string; name: string }[];
}

/** `unknown` = jeste jsme se serveru nezeptali (guard musi pockat). */
export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous';

/** Flagy nactene pri bootstrapu session (krok O1) - viz `ensureSession`. */
export interface SessionFlags {
  /** `pilot.menu` (@reos/flags): skryje polozky `preparing` z menu a presmeruje jejich cesty. */
  readonly pilotMenu: boolean;
  /** `pilot.showHiddenSections` (krok UX1): odemyka skupinu menu „Skrýt do napojení“. */
  readonly showHiddenSections: boolean;
  /**
   * Krok MOB1 bod 3: je „Nahlásit problém" na serveru zapojene
   * (`PODPORA_API_URL` + `PODPORA_TOKEN_REOS`)? Bez toho se tlacitko NEUKAZE —
   * zadani „zadna falesna funkce". Vychozi `false`: dokud server nerekne, ze
   * to funguje, nic nenabizime.
   */
  readonly supportReporting: boolean;
  /**
   * Krok D1 bod 3: prihlasena kancelar je UKAZKOVA (`tenants.slug = 'demo'`).
   * Pruh „Ukázková data. Nic se neodesílá." a pruvodce demem se ukazuji jen
   * podle tohohle — nikdy podle adresy, cookie nebo jmena kancelare.
   */
  readonly demo: boolean;
  /**
   * Krok DEMO7 bod 1: tahle instance je VEREJNE demo bez hesla
   * (`REOS_PUBLIC_DEMO=1` na serveru). Pruh pak pise, ze se data kazdou
   * hodinu vraceji do puvodniho stavu, a prevereni nabizi jako ukazku
   * zdarma. Vychozi `false`: dokud server nerekne, ze je to verejne demo,
   * nic takoveho netvrdime (na preview je `demo` bez `publicDemo`).
   */
  readonly publicDemo: boolean;
  /**
   * Krok KN4 bod 2: smi administrace zobrazit udaj z katastru
   * (`KN_OUTPUTS_ENABLED` na serveru)? Vychozi `false` je FAIL-CLOSED: § 53
   * katastralniho zakona je zakaz sireni bez souhlasu ČÚZK, takze „nevime"
   * musi znamenat „neukazuj". Volitelne zamerne: chybejici pole (starsi
   * volajici, testovaci fixtura) znamena VYPNUTO, takze zapomenuti nemuze
   * vest k zobrazeni udaju katastru.
   */
  readonly knOutputs?: boolean | undefined;
  /**
   * Krok KNT1 bod 2: jsou udaje katastru povolene jen VYJIMKOU nasi
   * interni testovaci kancelare (ne souhlasem ČÚZK)? Panel parcely nad nimi
   * pak tiskne vodoznak „INTERNÍ TEST — údaje z katastru, nešířit". Vychozi
   * `false` je bezpecny oba smery: bez vyjimky se nic netvrdi.
   */
  readonly knOutputsTest?: boolean | undefined;
}

const DEFAULT_FLAGS: SessionFlags = {
  pilotMenu: false,
  showHiddenSections: false,
  supportReporting: false,
  demo: false,
  publicDemo: false,
  knOutputs: false,
  knOutputsTest: false,
};

interface SessionState {
  readonly user: SessionUser | null;
  readonly tenant: TenantInfo | null;
  /**
   * Krok TRIAL1: zkusebni obdobi kancelare ze `GET /api/me`. `null` = kancelar
   * ho nema — pruh v hlavicce ani karta v Nastaveni pak netvrdi nic.
   */
  readonly trial: TrialStatus | null;
  readonly status: SessionStatus;
  readonly activeOfficeId: string | null;
  readonly pilotMenu: boolean;
  readonly showHiddenSections: boolean;
  readonly supportReporting: boolean;
  readonly demo: boolean;
  readonly publicDemo: boolean;
  readonly knOutputs: boolean;
  readonly knOutputsTest: boolean;
  readonly setSession: (
    user: SessionUser,
    tenant: TenantInfo | null,
    flags?: SessionFlags,
    trial?: TrialStatus | null,
  ) => void;
  /**
   * Krok PIL1 (vada V2): jmeno prihlaseneho cloveka po jeho zadani. Pozdrav
   * i avatar ctou `user.name`, takze se obe mista prekresli bez druheho
   * `GET /api/me`.
   */
  readonly setUserName: (name: string) => void;
  /**
   * Krok NAP3 bod 2: portret po ulozeni/smazani. Stejny duvod jako u jmena —
   * karta profilu (a pozdeji avatar) ho cte z `user.portrait`, takze se
   * prekresli bez druheho `GET /api/me`.
   */
  readonly setUserPortrait: (portrait: string | null) => void;
  readonly setAnonymous: () => void;
  readonly setActiveOffice: (officeId: string | null) => void;
  readonly clear: () => void;
}

export const useSession = create<SessionState>((set) => ({
  user: null,
  tenant: null,
  trial: null,
  status: 'unknown',
  activeOfficeId: null,
  pilotMenu: DEFAULT_FLAGS.pilotMenu,
  showHiddenSections: DEFAULT_FLAGS.showHiddenSections,
  supportReporting: DEFAULT_FLAGS.supportReporting,
  demo: DEFAULT_FLAGS.demo,
  publicDemo: DEFAULT_FLAGS.publicDemo,
  knOutputs: DEFAULT_FLAGS.knOutputs ?? false,
  knOutputsTest: DEFAULT_FLAGS.knOutputsTest ?? false,
  setSession: (user, tenant, flags = DEFAULT_FLAGS, trial = null) =>
    set({
      user,
      tenant,
      trial,
      status: 'authenticated',
      activeOfficeId: tenant ? (user.officeId ?? tenant.offices[0]?.id ?? null) : null,
      pilotMenu: flags.pilotMenu,
      showHiddenSections: flags.showHiddenSections,
      supportReporting: flags.supportReporting,
      demo: flags.demo,
      publicDemo: flags.publicDemo,
      knOutputs: flags.knOutputs ?? false,
      knOutputsTest: flags.knOutputsTest ?? false,
    }),
  setUserName: (name) =>
    set((state) => (state.user === null ? state : { user: { ...state.user, name } })),
  setUserPortrait: (portrait) =>
    set((state) => (state.user === null ? state : { user: { ...state.user, portrait } })),
  setAnonymous: () => set({ user: null, tenant: null, trial: null, status: 'anonymous', activeOfficeId: null, pilotMenu: DEFAULT_FLAGS.pilotMenu, showHiddenSections: DEFAULT_FLAGS.showHiddenSections, supportReporting: DEFAULT_FLAGS.supportReporting, demo: DEFAULT_FLAGS.demo, publicDemo: DEFAULT_FLAGS.publicDemo, knOutputs: DEFAULT_FLAGS.knOutputs ?? false, knOutputsTest: DEFAULT_FLAGS.knOutputsTest ?? false }),
  setActiveOffice: (officeId) => set({ activeOfficeId: officeId }),
  clear: () => set({ user: null, tenant: null, trial: null, status: 'unknown', activeOfficeId: null, pilotMenu: DEFAULT_FLAGS.pilotMenu, showHiddenSections: DEFAULT_FLAGS.showHiddenSections, supportReporting: DEFAULT_FLAGS.supportReporting, demo: DEFAULT_FLAGS.demo, publicDemo: DEFAULT_FLAGS.publicDemo, knOutputs: DEFAULT_FLAGS.knOutputs ?? false, knOutputsTest: DEFAULT_FLAGS.knOutputsTest ?? false }),
}));

/**
 * Vysledek dotazu na session (krok E2E7).
 *
 * TRI stavy, ne dva: „server neodpovedel" NENI „neprihlaseny". Do tohoto kroku
 * vracel `ensureSession` v obou pripadech `null` a router guard z toho udelal
 * presmerovani na `/login` — jedno 502 z API (pm2 restartoval preview API na
 * stropu pameti, viz REPORT-step-E2E7.md) tak uprostred prace ODHLASILO
 * uzivatele, ktery mel platnou session cookie. Uzivatel s rozpracovanou
 * obrazovkou prisel o kontext kvuli dvousekundovemu zadrhnuti serveru.
 */
export type SessionCheck =
  | { readonly status: 'authenticated'; readonly user: SessionUser }
  | { readonly status: 'anonymous' }
  | { readonly status: 'unavailable' };

/**
 * Jedno volani `GET /api/me` na start aplikace. Router guard i shell ho volaji
 * oba — druhy dostane rozpracovany slib, ne druhy request.
 */
let pending: Promise<SessionCheck> | null = null;

/**
 * JEDEN POKUS O BOOTSTRAP SESSION. Vyhodi, kdyz server neodpovedel — o tom,
 * jestli se pokus opakuje, rozhoduje `ensureSession` (`retryOnRateLimit`).
 */
async function loadSession(): Promise<SessionCheck> {
  const me = await fetchMe();
  if (me === null) {
    // Server REKL 401 — tohle je skutecne neprihlaseny uzivatel.
    useSession.getState().setAnonymous();
    return { status: 'anonymous' };
  }
  /*
   * Krok QAFIX13 — SERVER UZ REKL, ZE UZIVATEL JE PRIHLASENY, takze chyby
   * z jeho prohlizece uz maji komu patrit a odesilani se zapina TADY, ne az
   * ve shellu (`layout.tsx` to dela dal, je to idempotentni).
   *
   * Proc: shell se nemusi vykreslit vubec. Kdyz zbytek bootstrapu nebo
   * `beforeLoad` routy vyhodi, kresli se error boundary (`AppErrorScreen`),
   * ktera makleři slibuje „Chyba je zapsaná u nás" — a pritom hlaseni
   * zahodila, protoze `enabled` bylo dosud `false`. Presne to se stalo
   * 21. 10. 2026 11:49 (pruchod P2 krok 6, `/zakazky` v error boundary):
   * `client_error_events` o tom dve hodiny nemely ani radku.
   */
  startClientErrorReporting();
  // Session musi nest aktivni organizaci, jinak domenove API vraci 403
  // (viz `setActiveOrganization`). Delame to na jednom miste pri bootstrapu,
  // ne v kazde prihlasovaci obrazovce zvlast.
  if (me.tenant !== null) await setActiveOrganization(me.tenant.id);
  useSession.getState().setSession(me.user, me.tenant, me.flags, me.trial);
  // Krok UXV4p bod 3: „Velikost písma" a „Vzhled" patri uzivateli, takze
  // se po nacteni session srovnaji s tim, co ma ulozene na serveru.
  applyServerUiPreferences(me.ui);
  return { status: 'authenticated', user: me.user };
}

export async function ensureSession(): Promise<SessionCheck> {
  const state = useSession.getState();
  if (state.status === 'authenticated' && state.user !== null) {
    return { status: 'authenticated', user: state.user };
  }
  if (state.status === 'anonymous') return { status: 'anonymous' };
  /*
   * Krok QAFIX13 — JEDNO ZADRHNUTI SITE NENI „SERVER NEODPOVIDA".
   *
   * Bootstrap jsou DVA pozadavky (`GET /api/me` + `POST …/organization/
   * set-active`) a staci, aby selhal kterykoli: guard pak vyhodi
   * `appError.serverUnavailable` a cela obrazovka skonci v error boundary.
   * 21. 10. 2026 11:49:48 to na `/zakazky` potkalo pruchod P2 (krok 6):
   * `/api/me` dalo 200 a hned nato `set-active` spadlo na
   * `net::ERR_NETWORK_CHANGED` (docker veth) — makleřka se pred sekundu
   * nactenou obrazovkou dozvedela, ze „server teď neodpovídá".
   *
   * Pokus se proto OPAKUJE pres tutéz branu jako cteni seznamu
   * (`retryOnRateLimit`: sitova chyba a 429, cekani podle `Retry-After`).
   * Obe volani bootstrapu jsou IDEMPOTENTNI — `GET /api/me` je cteni
   * a `set-active` zapisuje porad tutéz aktivni organizaci —, takze druhy
   * pokus nic nezdvoji; vyjimka z pravidla „mutace se neopakuji" plati jen
   * pro tohle jedno volani a jen tady.
   */
  pending ??= retryOnRateLimit(loadSession)
    .catch((): SessionCheck => {
      // Server nedostupny (5xx, sitova chyba): session zustava NEZNAMA, aby to
      // guard mohl zkusit znovu, a volajici se dozvi `unavailable` — nikdy
      // `anonymous`. Odhlasit uzivatele smi jen server svym 401.
      return { status: 'unavailable' };
    })
    .finally(() => {
      pending = null;
    });
  return pending;
}

/** Po odhlaseni musi dalsi guard znovu na server. */
export function resetSession(): void {
  pending = null;
  useSession.getState().setAnonymous();
}

// ---- ulozene predvolby prohlizece -------------------------------------------

const COLLAPSED_SECTIONS_KEY = 'reos.nav.collapsedSections';

/**
 * Sbalene skupiny menu (BLOK 2.5). Jde o predvolbu prohlizece, ne o data.
 * Krok UXA1: kdyz jeste nikdo nic nesbalil/nerozbalil (klic v localStorage
 * chybi), platí `defaultCollapsed` — skupina „Další" zacina sbalena
 * (R.B. 1. 10.), ostatni volajici (bez parametru) chovani nemeni.
 */
export function readCollapsedNavSections(defaultCollapsed: readonly string[] = []): ReadonlySet<string> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_SECTIONS_KEY);
    if (raw === null) return new Set(defaultCollapsed);
    const ids: unknown = JSON.parse(raw);
    return new Set(Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return new Set(defaultCollapsed);
  }
}

export function writeCollapsedNavSections(ids: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify([...ids]));
  } catch {
    // localStorage nedostupny (private mode) — stav zustane jen v pameti
  }
}

const CASE_VIEW_KEY = 'reos.zakazky.view';

/**
 * Zvolene zobrazeni `/zakazky` (kanban / seznam / tabulka, krok O8). Zase jen
 * predvolba prohlizece, ne data — platnost hodnoty overuje volajici, tenhle
 * modul vraci, co v ulozisti stoji.
 */
export function readCaseViewPreference(): string | null {
  try {
    return window.localStorage.getItem(CASE_VIEW_KEY);
  } catch {
    return null;
  }
}

export function writeCaseViewPreference(view: string): void {
  try {
    window.localStorage.setItem(CASE_VIEW_KEY, view);
  } catch {
    // viz readCollapsedNavSections
  }
}

const CALENDAR_VIEW_KEY = 'reos.kalendar.view';

/**
 * Zvolene zobrazeni kalendare (den / tyden / mesic / seznam, krok FIX4 bod 2:
 * „volbu si pamatovat"). Stejny vzor jako `readCaseViewPreference` — jen
 * predvolba prohlizece, platnost hodnoty overuje volajici.
 */
export function readCalendarViewPreference(): string | null {
  try {
    return window.localStorage.getItem(CALENDAR_VIEW_KEY);
  } catch {
    return null;
  }
}

export function writeCalendarViewPreference(view: string): void {
  try {
    window.localStorage.setItem(CALENDAR_VIEW_KEY, view);
  } catch {
    // viz readCollapsedNavSections
  }
}

const DEMAND_VIEW_KEY = 'reos.poptavky.view';

/**
 * Krok UXL1 bod 3 — zvolene zobrazeni `/poptavky` (seznam / tabule). Stejny
 * vzor jako `readCaseViewPreference` — jen predvolba prohlizece, platnost
 * hodnoty overuje volajici.
 */
export function readDemandViewPreference(): string | null {
  try {
    return window.localStorage.getItem(DEMAND_VIEW_KEY);
  } catch {
    return null;
  }
}

export function writeDemandViewPreference(view: string): void {
  try {
    window.localStorage.setItem(DEMAND_VIEW_KEY, view);
  } catch {
    // viz readCollapsedNavSections
  }
}

const TASK_VIEW_KEY = 'reos.ukoly.view';

/**
 * Krok UXL1 bod 4 — zvolene zobrazeni `/ukoly` (seznam / tabule). Vychozi
 * zustava tabule (schvalena predloha v3, R.B. 3. 10.) — seznam je volba.
 */
export function readTaskViewPreference(): string | null {
  try {
    return window.localStorage.getItem(TASK_VIEW_KEY);
  } catch {
    return null;
  }
}

export function writeTaskViewPreference(view: string): void {
  try {
    window.localStorage.setItem(TASK_VIEW_KEY, view);
  } catch {
    // viz readCollapsedNavSections
  }
}

const FONT_SIZE_KEY = 'reos.fontSize';

/**
 * Krok UXR4 — hodnota atributu `data-font-size` na `<html>`
 * (`tokens-admin-v2.css`). Krok UXV4p pridava ctvrty stupen `largest`;
 * hodnoty zustavaji stejne, takze ulozene volby uzivatelu dal plati.
 *
 * Popisky stupnu („Menší · Normální · Větší · Největší") jsou v katalogu
 * zprav, ne tady — tohle jsou technicke hodnoty atributu a sloupce
 * `user.ui_font_size`.
 */
export const FONT_SIZE_VALUES = ['small', 'normal', 'large', 'largest'] as const;
export type FontSizePreference = (typeof FONT_SIZE_VALUES)[number];

export function isFontSizePreference(value: unknown): value is FontSizePreference {
  return typeof value === 'string' && (FONT_SIZE_VALUES as readonly string[]).includes(value);
}

/**
 * Velikost pisma administrace („Velikost písma" v nabidce profilu i
 * v Nastaveni).
 *
 * KROK UXV4p ZMENIL ULOZISTE: volba ted zije U UZIVATELE NA SERVERU
 * (`user.ui_font_size`, `GET`/`PATCH /api/me`), takze plati na vsech jeho
 * zarizenich — zadani bod 3. `localStorage` zustava jako KES, ze ktere se
 * atribut nastavi JESTE PRED prvnim vykreslenim; server by na to dorazil
 * pozde a obrazovka by pri startu blikla. Po nacteni session se obe hodnoty
 * srovnaji (`applyServerUiPreferences`). Tim padá ROZHODNUTI UXR4-1
 * i UXV4a-TEMA („uloziste na serveru neexistuje").
 *
 * Vychozi "normal", kdyz klic chybi nebo ma neplatnou hodnotu, rozhoduje
 * volajici (`readFontSizePreference() ?? 'normal'`).
 */
export function readFontSizePreference(): FontSizePreference | null {
  try {
    const raw = window.localStorage.getItem(FONT_SIZE_KEY);
    return isFontSizePreference(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeFontSizePreference(size: FontSizePreference): void {
  try {
    window.localStorage.setItem(FONT_SIZE_KEY, size);
  } catch {
    // viz readCollapsedNavSections
  }
}

/** Nastavi `data-font-size` na `<html>`. Jedine misto, ktere ten atribut pise. */
export function applyFontSize(size: FontSizePreference): void {
  document.documentElement.setAttribute('data-font-size', size);
}

const THEME_KEY = 'reos.theme';

/** Krok UXV4a — volba „Vzhled" (UX v4 § Svetly a tmavy vzhled). */
export const THEME_VALUES = ['light', 'dark', 'system'] as const;
export type ThemePreference = (typeof THEME_VALUES)[number];

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEME_VALUES as readonly string[]).includes(value);
}

/**
 * Vzhled administrace (zadani UXV4a, doplneni R.B. 4. 10. 06:16: „Nastavení
 * → Vzhled → Světlý · Tmavý · Podle systému, výchozí Podle systému").
 *
 * KROK UXV4p — stejne jako „Velikost pisma": volba zije U UZIVATELE NA
 * SERVERU (`user.ui_theme`) a `localStorage` je jen kes pro start bez
 * bliknuti. ROZHODNUTI UXV4a-TEMA („uloziste na serveru neexistuje") tim
 * padá — uloziste zalozila migrace `0008_identity_ui_preferences.sql`.
 *
 * `null` = uzivatel jeste nevolil; vychozi „system" rozhoduje volajici.
 */
export function readThemePreference(): ThemePreference | null {
  try {
    const raw = window.localStorage.getItem(THEME_KEY);
    return isThemePreference(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeThemePreference(theme: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_KEY, theme);
  } catch {
    // viz readCollapsedNavSections
  }
}

/** Hodnota atributu `data-scheme` na `<html>` — „system" se rozhoduje dotazem. */
export function resolveScheme(theme: ThemePreference): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    // Prohlizec bez `matchMedia` (nebo jsdom v testu) — svetly je vychozi.
    return 'light';
  }
}

/**
 * Nastavi `data-scheme` na `<html>`. Vola se JESTE PRED prvnim vykreslenim
 * (`main.tsx`), aby obrazovka pri startu neblikla svetlou paletou — stejny
 * vzor jako `data-font-size` z UXR4.
 */
export function applyTheme(theme: ThemePreference): void {
  document.documentElement.setAttribute('data-scheme', resolveScheme(theme));
}

/**
 * Sleduje zmenu systemoveho nastaveni, dokud je volba „Podle systému".
 * Vraci funkci pro odhlaseni (vola ji Nastaveni pri prepnuti).
 */
export function watchSystemScheme(onChange: () => void): () => void {
  try {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    if (typeof mql.addEventListener !== 'function') return () => undefined;
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  } catch {
    return () => undefined;
  }
}

/**
 * Vychozi hodnoty — tytez, jake ma server (`DEFAULT_USER_UI_PREFERENCES`).
 *
 * Krok WEBN3 (zadani bod 1): vzhled je vychozi SVETLY, ne „podle systému".
 * Hodnota musi sedet se serverem, jinak by `applyServerUiPreferences` nize
 * poslala volbu z prohlizece na server pokazde, kdyz se obe strany „jen
 * jeste nesynchronizovaly".
 */
const DEFAULT_FONT_SIZE: FontSizePreference = 'normal';
export const DEFAULT_THEME: ThemePreference = 'light';

/**
 * Krok UXV4p bod 3 — SROVNANI VOLBY SE SERVEREM po nacteni session.
 *
 * Poradi je zamerne: `main.tsx` nastavi atributy z `localStorage` jeste pred
 * prvnim vykreslenim (bez bliknuti), a teprve ted, kdyz prisla odpoved
 * `/api/me`, se uplatni to, co ma uzivatel ulozene U SEBE — proto plati na
 * vsech zarizenich. Kdyz se hodnoty rovnaji, nestane se nic (zadne
 * prekresleni).
 *
 * JEDNORAZOVY PRECHOD z doby, kdy volba zila jen v prohlizeci (UXR4/UXV4a):
 * kdyz server nese VYCHOZI hodnotu a v prohlizeci je jina, posle se do
 * serveru ta z prohlizece — jinak by pilotni uzivatel o svou volbu prisel.
 * Falesne pozitivni to byt nemuze: kazde ulozeni zapisuje OBE mista, takze
 * „server vychozi + prohlizec jiny" znamena jen „jeste se nesynchronizovalo".
 */
export function applyServerUiPreferences(ui: UiPreferences): void {
  const localFontSize = readFontSizePreference();
  const localTheme = readThemePreference();

  const pushFontSize = ui.fontSize === DEFAULT_FONT_SIZE && localFontSize !== null && localFontSize !== ui.fontSize;
  const pushTheme = ui.theme === DEFAULT_THEME && localTheme !== null && localTheme !== ui.theme;
  if (pushFontSize || pushTheme) {
    void saveUiPreferences({
      ...(pushFontSize ? { fontSize: localFontSize } : {}),
      ...(pushTheme ? { theme: localTheme } : {}),
    });
  }

  const fontSize = pushFontSize ? (localFontSize as FontSizePreference) : ui.fontSize;
  const theme = pushTheme ? (localTheme as ThemePreference) : ui.theme;
  writeFontSizePreference(fontSize);
  applyFontSize(fontSize);
  writeThemePreference(theme);
  applyTheme(theme);
}

/**
 * Krok UXV4p bod 3 — jedno misto, kterym jde ZMENA volby vzhledu: nastavi
 * atribut na `<html>` (zivy nahled bez nacteni stranky), ulozi do kese
 * prohlizece (start bez bliknuti) a posle k uzivateli na server (plati na
 * vsech zarizenich). Vola to prepinac v nabidce profilu i v Nastaveni —
 * dve kopie teze posloupnosti by se rozesly.
 */
export function chooseFontSize(size: FontSizePreference): void {
  applyFontSize(size);
  writeFontSizePreference(size);
  void saveUiPreferences({ fontSize: size });
}

export function chooseTheme(theme: ThemePreference): void {
  applyTheme(theme);
  writeThemePreference(theme);
  void saveUiPreferences({ theme });
}

const CONTRACT_WHERE_HINT_KEY = 'reos.smlouvy.whereHint';

/**
 * Krok UXD1 bod 2 — zavrena lista „kam co psat" v editoru smlouvy. Zase jen
 * predvolba prohlizece (jeden priznak, zadna data): makler ji zavre jednou
 * a pri dalsim otevreni smlouvy uz ji nevidi. `localStorage` smi v `src/**`
 * jen tenhle modul (krok S3), proto je cteni i zapis tady a ne v obrazovce.
 */
export function readContractWhereHintDismissed(): boolean {
  try {
    return window.localStorage.getItem(CONTRACT_WHERE_HINT_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeContractWhereHintDismissed(): void {
  try {
    window.localStorage.setItem(CONTRACT_WHERE_HINT_KEY, '1');
  } catch {
    // viz readCollapsedNavSections
  }
}

const TRIAL_NOTICE_ACK_KEY = 'reos.trial.noticeAck';

/**
 * Krok PRO1 — UPOZORNENI NA KONEC ZKUSEBNIHO OBDOBI VZATE NA VEDOMI.
 *
 * Ulozeny je PRAH (7 nebo 1), a klic nese KONEC obdobi, takze nove zkusebni
 * obdobi upozorneni zase ukaze. Je to predvolba zobrazeni („tuhle zpravu uz
 * jsem videl"), ne data: prahy, datum ani stav platby tady nezijou — ty
 * chodi ze serveru (`trial` v `GET /api/me`).
 */
export function readTrialNoticeAck(endsAt: string | null): number | null {
  if (endsAt === null) return null;
  try {
    const raw = window.localStorage.getItem(`${TRIAL_NOTICE_ACK_KEY}.${endsAt}`);
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    // viz readCollapsedNavSections — upozorneni se pak jen neschova
    return null;
  }
}

export function writeTrialNoticeAck(endsAt: string, threshold: number): void {
  try {
    window.localStorage.setItem(`${TRIAL_NOTICE_ACK_KEY}.${endsAt}`, String(threshold));
  } catch {
    // viz readCollapsedNavSections
  }
}
